import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  createColumnHelper,
  flexRender,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  Typography,
  TextField,
  TableSortLabel,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckIcon from "@mui/icons-material/Check";

import api from '../utils/api';

function CSVViewer() {
  const [files, setFiles] = useState([]);
  const [csvData, setCsvData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [globalFilter, setGlobalFilter] = useState("");
  const [filename, setFilename] = useState("");
  const [headers, setHeaders] = useState([]);
  const [data, setData] = useState([]);
  const [csvString, setCSVString] = useState("");

  useEffect(() => {
    fetchFiles();
  }, []);

  const fetchFiles = () => {
    setLoading(true);
    axios
      .get("/files")
      .then((res) => {
        setFiles(res.data);
        if (res.data.length > 0) {
          loadFile(res.data[0]);
        }
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  };

  const loadFile = (filename) => {
    setLoading(true);
    api.get(`/load/${filename}`)
      .then(response => {
        setHeaders(response.data.headers);
        setData(response.data.data);
        setCSVString(response.data.csvString);
        setLoading(false);
      })
      .catch(error => {
        console.error('Erreur lors du chargement du fichier:', error);
        setLoading(false);
      });
  };

  const markAsAnalyzed = (index) => {
    const updatedData = [...csvData];
    updatedData[index].Statut = "Analyser";
    setCsvData(updatedData);

    axios
      .post("/save", updatedData)
      .then(() => console.log("Statut mis à jour"))
      .catch((err) => console.error(err));
  };

  const columnHelper = createColumnHelper();
  const columns =
    csvData.length > 0
      ? Object.keys(csvData[0]).map((key) =>
          columnHelper.accessor(key, {
            header: () => key,
            cell: ({ getValue, row }) => {
              const value = getValue();
              if (key === "URL") {
                return (
                  <a
                    style={{ textDecoration: "none" }}
                    href={value}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    🔗
                  </a>
                );
              }
              return value;
            },
            sortingFn: "alphanumeric",
          })
        )
      : [];

  columns.push(
    columnHelper.display({
      id: "actions",
      header: () => "Actions",
      cell: ({ row }) => (
        <Button
          variant="contained"
          color="success"
          size="small"
          startIcon={<CheckIcon />}
          onClick={() => markAsAnalyzed(row.index)}
          disabled={row.original.Statut === "Analyser"}
        >
          Analyser
        </Button>
      ),
    })
  );

  const table = useReactTable({
    data: csvData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: "includesString",
    state: {
      globalFilter,
    },
    onGlobalFilterChange: setGlobalFilter,
  });

  return (
    <div style={{ padding: "20px", maxWidth: "1200px", margin: "0 auto" }}>
      <Typography variant="h4" gutterBottom>
        Visualisation et Consultation des Données CSV
      </Typography>

      <TextField
        label="Rechercher..."
        variant="outlined"
        fullWidth
        style={{ marginBottom: "20px" }}
        value={globalFilter}
        onChange={(e) => setGlobalFilter(e.target.value)}
      />

      <Button
        variant="contained"
        color="secondary"
        startIcon={<RefreshIcon />}
        onClick={fetchFiles}
        style={{ marginBottom: "20px" }}
      >
        Rafraîchir
      </Button>

      {loading && (
        <CircularProgress style={{ display: "block", margin: "20px auto" }} />
      )}

      {csvData.length > 0 && (
        <TableContainer component={Paper}>
          <Table>
            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </div>
  );
}

export default CSVViewer;
